import type { CursorPresence, MapViewPresence, MapViewState, Measurement, SavedTarget } from "../types";

export type MapPoint = { lat: number; lon: number };

export type MapFitAction =
  | { kind: "noop" }
  | { kind: "center"; point: MapPoint }
  | { kind: "fitBounds"; points: MapPoint[] }
  | { kind: "applyRemoteView"; view: MapViewState };

const isFinitePoint = (point: { lat: number; lon: number }) =>
  typeof point.lat === "number" &&
  Number.isFinite(point.lat) &&
  typeof point.lon === "number" &&
  Number.isFinite(point.lon);

const isFiniteMapView = (view: MapViewPresence) =>
  typeof view.lat === "number" &&
  Number.isFinite(view.lat) &&
  typeof view.lon === "number" &&
  Number.isFinite(view.lon) &&
  typeof view.zoom === "number" &&
  Number.isFinite(view.zoom);

export const collectPointsForFit = ({
  measurements,
  savedTargets,
  overallEstimate,
  distanceCandidates,
  consensusEstimates
}: {
  measurements: Measurement[];
  savedTargets: SavedTarget[];
  overallEstimate: MapPoint | null;
  distanceCandidates: MapPoint[];
  consensusEstimates: MapPoint[];
}): MapPoint[] => {
  const points: MapPoint[] = [];
  measurements.forEach((measurement) => {
    if (isFinitePoint(measurement)) {
      points.push({ lat: measurement.lat, lon: measurement.lon });
    }
  });
  savedTargets.forEach((target) => {
    if (isFinitePoint(target)) {
      points.push({ lat: target.lat, lon: target.lon });
    }
  });
  if (overallEstimate && isFinitePoint(overallEstimate)) {
    points.push(overallEstimate);
  }
  distanceCandidates.forEach((candidate) => {
    if (isFinitePoint(candidate)) {
      points.push(candidate);
    }
  });
  consensusEstimates.forEach((candidate) => {
    if (isFinitePoint(candidate)) {
      points.push(candidate);
    }
  });
  return points;
};

export const getPointFitAction = (points: MapPoint[]): MapFitAction => {
  if (points.length === 0) {
    return { kind: "noop" };
  }
  if (points.length === 1) {
    return { kind: "center", point: points[0] };
  }
  return { kind: "fitBounds", points };
};

export const getUserFitAction = ({
  localUserId,
  presenceUserIds,
  cursorEntries,
  mapViewEntries,
  extraParticipantIds = []
}: {
  localUserId: string;
  presenceUserIds: string[];
  cursorEntries: CursorPresence[];
  mapViewEntries: MapViewPresence[];
  extraParticipantIds?: string[];
}): MapFitAction => {
  const participantIds = new Set<string>(
    presenceUserIds.filter((userId) => userId !== localUserId)
  );
  extraParticipantIds.forEach((userId) => {
    if (userId && userId !== localUserId) {
      participantIds.add(userId);
    }
  });

  const others = Array.from(participantIds);

  if (others.length === 0) {
    return { kind: "noop" };
  }

  if (others.length === 1) {
    const mapView = mapViewEntries.find(
      (entry) => entry.userId === others[0] && isFiniteMapView(entry)
    );
    if (!mapView) {
      return { kind: "noop" };
    }
    return {
      kind: "applyRemoteView",
      view: {
        lat: mapView.lat,
        lon: mapView.lon,
        zoom: mapView.zoom
      }
    };
  }

  const points = cursorEntries
    .filter(
      (cursor) =>
        participantIds.has(cursor.userId) &&
        cursor.active &&
        typeof cursor.lat === "number" &&
        Number.isFinite(cursor.lat) &&
        typeof cursor.lon === "number" &&
        Number.isFinite(cursor.lon)
    )
    .map((cursor) => ({ lat: cursor.lat as number, lon: cursor.lon as number }));

  if (points.length === 0) {
    return { kind: "noop" };
  }

  if (points.length === 1) {
    return { kind: "center", point: points[0] };
  }

  return { kind: "fitBounds", points };
};
